import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Archive, AlertCircle, User, Clock, X, Mail, Loader2, Timer, Zap, Star } from "lucide-react";
import { format } from "date-fns";
import { getEmails, batchUpdate, updateEmail } from "../../services/emailApi";

// -- Colour palette -----------------------------------------------------------
const PALETTE = [
  { bg: "#3B82F6", fill: "#DBEAFE", text: "#1D4ED8" },
  { bg: "#10B981", fill: "#D1FAE5", text: "#065F46" },
  { bg: "#F59E0B", fill: "#FEF3C7", text: "#92400E" },
  { bg: "#8B5CF6", fill: "#EDE9FE", text: "#5B21B6" },
  { bg: "#EC4899", fill: "#FCE7F3", text: "#9D174D" },
  { bg: "#06B6D4", fill: "#CFFAFE", text: "#155E75" },
  { bg: "#F97316", fill: "#FFEDD5", text: "#9A3412" },
  { bg: "#14B8A6", fill: "#CCFBF1", text: "#134E4A" },
  { bg: "#6366F1", fill: "#E0E7FF", text: "#3730A3" },
  { bg: "#84CC16", fill: "#ECFCCB", text: "#3F6212" },
  { bg: "#EF4444", fill: "#FEE2E2", text: "#991B1B" },
  { bg: "#64748B", fill: "#F1F5F9", text: "#1E293B" },
];

function colorFor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function senderLabel(raw, maxLen = 28) {
  if (!raw) return "Unknown";
  const label = raw.split("<")[0].trim().replace(/"/g, "").substring(0, maxLen) || raw.substring(0, maxLen);
  return label;
}

// -- Sankey constants ---------------------------------------------------------
const NODE_W = 26;
const PAD = 8;
const MIN_NODE_H = 6;

// -- Sankey layout ------------------------------------------------------------
function buildSankey(emails, W, H) {
  if (!emails.length || W < 50 || H < 50) return { cats: [], senders: [], links: [] };

  const catMap = new Map();
  const sndCounts = new Map();

  for (const e of emails) {
    const cat = e.llm_category || "Uncategorized";
    const snd = senderLabel(e.from_address);
    if (!catMap.has(cat)) catMap.set(cat, new Map());
    const inner = catMap.get(cat);
    if (!inner.has(snd)) inner.set(snd, []);
    inner.get(snd).push(e);
    sndCounts.set(snd, (sndCounts.get(snd) || 0) + 1);
  }

  const PAD_H = 60;
  const availH = H - PAD_H;
  const total = emails.length;

  const catEntries = [...catMap.entries()].sort((a, b) => {
    const ac = [...a[1].values()].reduce((s, v) => s + v.length, 0);
    const bc = [...b[1].values()].reduce((s, v) => s + v.length, 0);
    return bc - ac;
  });

  const sndEntries = [...sndCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 30);

  const maxNodes = Math.max(catEntries.length, sndEntries.length);
  const effectivePad = maxNodes > 1 ? Math.min(PAD, Math.max(2, (availH * 0.3) / (maxNodes - 1))) : PAD;
  const totalPadSpace = maxNodes > 1 ? (maxNodes - 1) * effectivePad : 0;
  const spaceForNodes = Math.max(availH - totalPadSpace, maxNodes * 2);
  const effectiveMinH = Math.max(2, Math.min(MIN_NODE_H, spaceForNodes / maxNodes));
  const nodeH = (n) => Math.max(effectiveMinH, (n / total) * spaceForNodes);

  const MARGIN = W < 400 ? 100 : W < 500 ? 110 : W < 700 ? 120 : 190;
  const LEFT = MARGIN;
  const RIGHT = W - MARGIN;

  const cats = [];
  {
    const nodes = catEntries.map(([name, sMap]) => {
      const count = [...sMap.values()].reduce((s, v) => s + v.length, 0);
      return { name, count, color: colorFor(name), x: LEFT, y: 0, h: nodeH(count) };
    });
    const totalH = nodes.reduce((s, n) => s + n.h, 0) + (nodes.length - 1) * effectivePad;
    let y = PAD_H / 2 + Math.max(0, (availH - totalH) / 2);
    for (const n of nodes) { n.y = y; y += n.h + effectivePad; cats.push(n); }
  }

  const senders = [];
  {
    const nodes = sndEntries.map(([name, count]) => ({ name, count, x: RIGHT, y: 0, h: nodeH(count) }));
    const totalH = nodes.reduce((s, n) => s + n.h, 0) + (nodes.length - 1) * effectivePad;
    let y = PAD_H / 2 + Math.max(0, (availH - totalH) / 2);
    for (const n of nodes) { n.y = y; y += n.h + effectivePad; senders.push(n); }
  }

  const catOffsets = new Map(cats.map(n => [n.name, 0]));
  const sndOffsets = new Map(senders.map(n => [n.name, 0]));
  const links = [];

  for (const cat of cats) {
    const inner = catMap.get(cat.name);
    const sorted = [...inner.entries()].sort((a, b) => b[1].length - a[1].length);
    for (const [sndName, linkEmails] of sorted) {
      const snd = senders.find(s => s.name === sndName);
      if (!snd) continue;
      const count = linkEmails.length;
      const lh = Math.max(1, nodeH(count));
      const catOff = catOffsets.get(cat.name) ?? 0;
      const sndOff = sndOffsets.get(sndName) ?? 0;
      links.push({ source: cat, target: snd, emails: linkEmails, count, lh, sy: cat.y + catOff + lh / 2, ty: snd.y + sndOff + lh / 2 });
      catOffsets.set(cat.name, catOff + lh);
      sndOffsets.set(sndName, sndOff + lh);
    }
  }

  return { cats, senders, links };
}

function linkPath(sx, sy, tx, ty) {
  const mx = (sx + tx) / 2;
  return `M${sx},${sy} C${mx},${sy} ${mx},${ty} ${tx},${ty}`;
}

// -- Time estimate chip -------------------------------------------------------
function TimeChip({ estimate }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
      <Timer className="w-2.5 h-2.5" />
      {estimate}
    </span>
  );
}

// -- Main component -----------------------------------------------------------
export default function FlowsView({ account, onArchive: _onArchive, onStar: _onStar }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selection, setSelection] = useState(null);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const [archivingIds, setArchivingIds] = useState(new Set());
  const [archivingAll, setArchivingAll] = useState(false);

  // Fetch all emails for flows visualization
  useEffect(() => {
    async function fetchEmails() {
      setLoading(true);
      try {
        const params = { folder: 'inbox', limit: 500 };
        if (account && account !== 'all') params.account = account;
        const data = await getEmails(params);
        setEmails(data.emails || []);
      } catch (err) {
        console.error('Failed to load emails for flows:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchEmails();
  }, [account]);

  const onArchive = useCallback(async (ids) => {
    const idList = Array.isArray(ids) ? ids : [ids];
    try {
      await batchUpdate(idList, 'archive');
      setEmails(prev => prev.filter(e => !idList.includes(e.id)));
    } catch (err) {
      console.error('Archive failed:', err);
    }
  }, []);

  const onStar = useCallback(async (id) => {
    try {
      const email = emails.find(e => e.id === id);
      if (email) {
        await updateEmail(id, { is_starred: !email.is_starred });
        setEmails(prev => prev.map(e => e.id === id ? { ...e, is_starred: !e.is_starred } : e));
      }
    } catch (err) {
      console.error('Star failed:', err);
    }
  }, [emails]);

  const handleArchiveSingle = useCallback((id) => {
    if (!onArchive) return;
    setArchivingIds(prev => new Set(prev).add(id));
    Promise.resolve(onArchive(id)).finally(() => {
      setArchivingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    });
  }, [onArchive]);

  const handleArchiveBatch = useCallback((ids) => {
    if (!onArchive) return;
    setArchivingAll(true);
    setArchivingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
    Promise.all(ids.map(id => onArchive(id))).then(() => {
      setSelection(null);
      setSelectedEmailId(null);
    }).finally(() => {
      setArchivingAll(false);
      setArchivingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
    });
  }, [onArchive]);

  const handleStar = useCallback((id, currentStarred) => {
    if (onStar) onStar(id, !currentStarred);
  }, [onStar]);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: Math.max(width, 200), h: Math.max(height, 200) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const panelOpen = selection !== null;
  const svgW = dims.w;

  const { cats, senders, links } = useMemo(() => buildSankey(emails, svgW, dims.h), [emails, svgW, dims.h]);
  const svgH = dims.h;

  // Derive selected emails based on selection type
  const { selectedEmails, drawerTitle, drawerColor } = useMemo(() => {
    if (!selection) return { selectedEmails: [], drawerTitle: "", drawerColor: PALETTE[0] };

    let filtered;
    let title;
    let color;

    if (selection.type === "cat") {
      filtered = emails.filter(e => (e.llm_category || "Uncategorized") === selection.name);
      title = selection.name;
      color = colorFor(selection.name);
    } else if (selection.type === "sender") {
      filtered = emails.filter(e => senderLabel(e.from_address) === selection.name);
      title = selection.name;
      const catCounts = new Map();
      for (const e of filtered) {
        const cat = e.llm_category || "Uncategorized";
        catCounts.set(cat, (catCounts.get(cat) || 0) + 1);
      }
      const topCat = [...catCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "Uncategorized";
      color = colorFor(topCat);
    } else {
      // actions
      filtered = emails.filter(e => e.llm_action_required);
      title = "Action Required";
      color = { bg: "#EF4444", fill: "#FEE2E2", text: "#991B1B" };
    }

    const sorted = [...filtered].sort((a, b) => {
      if (a.llm_action_required && !b.llm_action_required) return -1;
      if (!a.llm_action_required && b.llm_action_required) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return { selectedEmails: sorted, drawerTitle: title, drawerColor: color };
  }, [emails, selection]);

  const handleSelect = useCallback((s) => {
    setSelection(prev => {
      if (!prev || !s) return s;
      if (prev.type === s?.type && (prev.type === "actions" || prev.name === s.name)) return null;
      return s;
    });
    setSelectedEmailId(null);
  }, []);

  const isLinkHighlighted = useCallback((lk) => {
    if (selection?.type === "cat") return lk.source.name === selection.name;
    if (selection?.type === "sender") return lk.target.name === selection.name;
    if (selection?.type === "actions") return lk.emails.some(e => e.llm_action_required);
    if (hoveredNode?.type === "cat") return lk.source.name === hoveredNode.name;
    if (hoveredNode?.type === "sender") return lk.target.name === hoveredNode.name;
    return hoveredLink === lk;
  }, [selection, hoveredNode, hoveredLink]);

  const getLinkOpacity = useCallback((lk) => {
    const highlighted = isLinkHighlighted(lk);
    if (selection || hoveredNode || hoveredLink) return highlighted ? 0.6 : 0.06;
    return 0.25;
  }, [selection, hoveredNode, hoveredLink, isLinkHighlighted]);

  // Email detail modal
  const selectedEmail = emails.find(e => e.id === selectedEmailId) ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <Mail size={32} className="mb-2" />
        <p>No emails to visualize</p>
        <p className="text-sm">Sync an external account or wait for incoming emails</p>
      </div>
    );
  }

  return (
    <div className="relative flex-1 overflow-hidden" ref={containerRef}>
      {/* SVG Sankey */}
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <svg
          ref={svgRef}
          width={svgW}
          height={svgH}
          style={{ display: "block", background: "hsl(210 20% 97%)", cursor: "default" }}
          onClick={() => { setSelection(null); setSelectedEmailId(null); }}
        >
          {svgW >= 500 && (
            <>
              <text x={cats[0]?.x ?? 190} y={22} textAnchor="start" fontSize={11} fontWeight={600} fill="#64748B" letterSpacing={0.8}>CATEGORIES</text>
              <text x={(senders[0]?.x ?? (svgW - 190)) + NODE_W + 8} y={22} textAnchor="start" fontSize={11} fontWeight={600} fill="#64748B" letterSpacing={0.8}>SENDERS</text>
            </>
          )}

          <defs>
            <pattern id="grid" width={28} height={28} patternUnits="userSpaceOnUse">
              <circle cx={14} cy={14} r={0.8} fill="#CBD5E1" />
            </pattern>
          </defs>
          <rect width={svgW} height={svgH} fill="url(#grid)" />

          {/* Links */}
          {links.map((lk, i) => (
            <path
              key={i}
              d={linkPath(lk.source.x + NODE_W, lk.sy, lk.target.x, lk.ty)}
              fill="none"
              stroke={lk.source.color.bg}
              strokeWidth={Math.max(lk.lh, 1.5)}
              strokeLinecap="butt"
              opacity={getLinkOpacity(lk)}
              style={{ cursor: "pointer", transition: "opacity 0.15s" }}
              onMouseEnter={() => { setHoveredLink(lk); setHoveredNode(null); }}
              onMouseLeave={() => setHoveredLink(null)}
              onClick={(e) => { e.stopPropagation(); handleSelect({ type: "cat", name: lk.source.name }); }}
            />
          ))}

          {/* Category nodes */}
          {cats.map(cat => {
            const isSel = selection?.type === "cat" && selection.name === cat.name;
            const isHov = hoveredNode?.type === "cat" && hoveredNode.name === cat.name;
            return (
              <g
                key={cat.name}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredNode({ type: "cat", name: cat.name })}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={(e) => { e.stopPropagation(); handleSelect({ type: "cat", name: cat.name }); }}
              >
                <rect x={cat.x} y={cat.y} width={NODE_W} height={cat.h}
                  fill={isSel || isHov ? cat.color.bg : cat.color.fill}
                  rx={4} stroke={cat.color.bg} strokeWidth={isSel ? 2 : 1} />
                <text x={cat.x - 8} y={cat.y + cat.h / 2} textAnchor="end" dominantBaseline="middle"
                  fontSize={Math.min(svgW < 500 ? 10 : 13, Math.max(9, cat.h * 0.55))}
                  fontWeight={isSel ? 700 : 500} fill={isSel ? cat.color.bg : "#374151"}>
                  {svgW < 500 ? cat.name.substring(0, 10) : cat.name}
                </text>
                {cat.h > 24 && (
                  <text x={cat.x - 8} y={cat.y + cat.h / 2 + 13} textAnchor="end" dominantBaseline="middle"
                    fontSize={10} fill="#94A3B8">{cat.count}</text>
                )}
              </g>
            );
          })}

          {/* Sender nodes */}
          {senders.map(snd => {
            const isSel = selection?.type === "sender" && selection.name === snd.name;
            const isHov = hoveredNode?.type === "sender" && hoveredNode.name === snd.name;
            return (
              <g
                key={snd.name}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredNode({ type: "sender", name: snd.name })}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={(e) => { e.stopPropagation(); handleSelect({ type: "sender", name: snd.name }); }}
              >
                <rect x={snd.x} y={snd.y} width={NODE_W} height={snd.h}
                  fill={isSel || isHov ? "#64748B" : "#F1F5F9"}
                  rx={4} stroke={isSel ? "#475569" : "#CBD5E1"} strokeWidth={isSel ? 2 : 1} />
                <text x={snd.x + NODE_W + 8} y={snd.y + snd.h / 2} dominantBaseline="middle"
                  fontSize={Math.min(svgW < 500 ? 9 : 12, Math.max(8, snd.h * 0.55))}
                  fontWeight={isSel ? 700 : 500} fill={isSel ? "#1E293B" : "#374151"}>
                  {svgW < 400 ? senderLabel(snd.name, 10) : svgW < 500 ? senderLabel(snd.name, 14) : snd.name}
                </text>
                {snd.h > 24 && (
                  <text x={snd.x + NODE_W + 8} y={snd.y + snd.h / 2 + 13} dominantBaseline="middle"
                    fontSize={10} fill="#94A3B8">{snd.count}</text>
                )}
              </g>
            );
          })}

          {/* Hover tooltip for links */}
          {hoveredLink && !hoveredNode && (
            <g>
              <rect
                x={(hoveredLink.source.x + NODE_W + hoveredLink.target.x) / 2 - 64}
                y={(hoveredLink.sy + hoveredLink.ty) / 2 - 20}
                width={128} height={36} rx={6}
                fill="white" stroke="#E2E8F0" strokeWidth={1}
                filter="drop-shadow(0 2px 6px rgba(0,0,0,0.1))"
              />
              <text x={(hoveredLink.source.x + NODE_W + hoveredLink.target.x) / 2}
                y={(hoveredLink.sy + hoveredLink.ty) / 2 - 5}
                textAnchor="middle" fontSize={11} fontWeight={600} fill="#1E293B">
                {hoveredLink.source.name}
              </text>
              <text x={(hoveredLink.source.x + NODE_W + hoveredLink.target.x) / 2}
                y={(hoveredLink.sy + hoveredLink.ty) / 2 + 10}
                textAnchor="middle" fontSize={10} fill="#64748B">
                {hoveredLink.count} email{hoveredLink.count !== 1 ? "s" : ""} → {hoveredLink.target.name}
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Side drawer (overlay) */}
      {selection !== null && (
        <div className="absolute top-0 right-0 bottom-0 flex flex-col border-l border-gray-200 bg-white shadow-xl z-10 w-full sm:w-[420px]">
          {/* Drawer header */}
          <div
            className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 flex-shrink-0"
            style={{ borderTop: `3px solid ${drawerColor.bg}` }}
          >
            <div
              className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold"
              style={{ background: drawerColor.fill, color: drawerColor.text }}
            >
              {selection.type === "actions"
                ? <AlertCircle className="w-4 h-4" />
                : selectedEmails.length}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {selection.type === "actions" ? "Action Required" : drawerTitle}
              </h3>
              <p className="text-xs text-gray-500">
                {selection.type === "actions" ? (
                  <span className="text-red-500 font-medium">{selectedEmails.length} email{selectedEmails.length !== 1 ? "s" : ""} need attention</span>
                ) : (
                  <>
                    {selectedEmails.filter(e => e.llm_action_required).length > 0 && (
                      <span className="text-red-500 font-medium">
                        {selectedEmails.filter(e => e.llm_action_required).length} action needed ·{" "}
                      </span>
                    )}
                    {selectedEmails.length} email{selectedEmails.length !== 1 ? "s" : ""}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                className="inline-flex items-center h-7 px-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                onClick={() => handleArchiveBatch(selectedEmails.map(e => e.id))}
                disabled={archivingAll || selectedEmails.length === 0}
              >
                {archivingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1" />}
                {archivingAll ? "Archiving..." : "Archive all"}
              </button>
              <button
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                onClick={() => { setSelection(null); setSelectedEmailId(null); }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Email list */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {selectedEmails.map(email => {
              const snd = senderLabel(email.from_address);
              const hasAction = email.llm_action_required && email.llm_action;
              return (
                <div
                  key={email.id}
                  className={`group flex flex-col gap-1.5 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${hasAction ? "bg-red-50/60" : ""}`}
                  onClick={() => setSelectedEmailId(email.id)}
                >
                  {/* Top row: sender + date */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-3 h-3 text-gray-500" />
                      </div>
                      <span className="text-xs font-semibold text-gray-900 truncate">{snd}</span>
                    </div>
                    <span className="text-[10px] text-gray-500 whitespace-nowrap flex-shrink-0">
                      {email.created_at ? format(new Date(email.created_at), "MMM d") : ""}
                    </span>
                  </div>

                  {/* Subject */}
                  <p className="text-sm font-medium text-gray-900 line-clamp-1 pl-8">
                    {email.subject}
                  </p>

                  {/* Action box -- shown for action-required emails */}
                  {hasAction && (
                    <div className="ml-8 mt-0.5 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
                      <Zap className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-red-700 leading-snug">{email.llm_action}</p>
                        {email.llm_time_estimate && (
                          <div className="mt-1">
                            <TimeChip estimate={email.llm_time_estimate} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* AI summary (shown when no action box) */}
                  {!hasAction && email.llm_summary && (
                    <p className="text-xs text-gray-500 line-clamp-2 pl-8 leading-relaxed">
                      {email.llm_summary}
                    </p>
                  )}

                  {/* Action buttons (hover) */}
                  <div className="pl-8 mt-0.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      className="inline-flex items-center h-6 text-xs px-2 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                      onClick={e => { e.stopPropagation(); setSelectedEmailId(email.id); }}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      View
                    </button>
                    <button
                      className={`inline-flex items-center h-6 text-xs px-2 border rounded-md transition-colors ${email.is_starred ? "border-amber-300 text-amber-600 bg-amber-50" : "border-gray-200 hover:border-amber-300 hover:text-amber-600"}`}
                      onClick={e => { e.stopPropagation(); handleStar(email.id, email.is_starred); }}
                    >
                      <Star className={`w-3 h-3 mr-1 ${email.is_starred ? "fill-amber-400" : ""}`} />
                      {email.is_starred ? "Starred" : "Star"}
                    </button>
                    <button
                      className="inline-flex items-center h-6 text-xs px-2 border border-gray-200 rounded-md hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                      onClick={e => { e.stopPropagation(); handleArchiveSingle(email.id); }}
                      disabled={archivingIds.has(email.id)}
                    >
                      {archivingIds.has(email.id) ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Archive className="w-3 h-3 mr-1" />}
                      {archivingIds.has(email.id) ? "Archiving..." : "Archive"}
                    </button>
                  </div>
                </div>
              );
            })}

            {selectedEmails.length === 0 && (
              <div className="flex flex-col items-center justify-center h-40 gap-2 text-gray-500">
                <Mail className="w-8 h-8 opacity-20" />
                <p className="text-sm">No emails here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full email modal */}
      {selectedEmailId !== null && selectedEmail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.45)" }}
          onClick={() => setSelectedEmailId(null)}
        >
          <div
            className="relative bg-white sm:rounded-2xl shadow-2xl flex flex-col w-full h-full sm:w-auto sm:h-auto"
            style={{ maxWidth: "min(780px, 96vw)", maxHeight: "100vh" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="px-6 pt-5 pb-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 leading-snug mb-2 pr-6">
                    {selectedEmail.subject ?? "Loading..."}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {senderLabel(selectedEmail.from_address)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {selectedEmail.created_at ? format(new Date(selectedEmail.created_at), "MMM d, yyyy h:mm a") : ""}
                    </span>
                  </div>

                  {/* Action details in modal */}
                  {selectedEmail.llm_action_required && selectedEmail.llm_action && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                      <Zap className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-red-700 mb-1">{selectedEmail.llm_action}</p>
                        <div className="flex items-center gap-2">
                          {selectedEmail.llm_time_estimate && <TimeChip estimate={selectedEmail.llm_time_estimate} />}
                          <span className="text-[10px] text-red-400">Action required</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedEmail.llm_summary && !selectedEmail.llm_action_required && (
                    <p className="mt-2 text-xs text-gray-500 bg-gray-100 rounded-md px-3 py-2 italic leading-relaxed">
                      AI: {selectedEmail.llm_summary}
                    </p>
                  )}
                </div>
                <button
                  className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  onClick={() => setSelectedEmailId(null)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {selectedEmail.body_html ? (
                <iframe
                  srcDoc={`<base target="_blank">${selectedEmail.body_html}`}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  className="w-full border-0"
                  style={{ minHeight: 400, height: "100%", display: "block" }}
                  title="Email content"
                />
              ) : selectedEmail.body_text ? (
                <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed p-6 font-sans">
                  {selectedEmail.body_text}
                </pre>
              ) : (
                <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-500">
                  <Mail className="w-8 h-8 opacity-30" />
                  <p className="text-sm">No content available</p>
                  {selectedEmail.body_preview && (
                    <p className="text-xs max-w-md text-center px-6 leading-relaxed opacity-70">{selectedEmail.body_preview}</p>
                  )}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-2xl">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  className={`inline-flex items-center h-8 text-sm px-3 border rounded-md transition-colors ${selectedEmail.is_starred ? "border-amber-300 text-amber-600 bg-amber-50" : "border-gray-200 hover:bg-gray-50"}`}
                  onClick={() => handleStar(selectedEmailId, selectedEmail.is_starred)}
                >
                  <Star className={`w-3.5 h-3.5 mr-1.5 ${selectedEmail.is_starred ? "fill-amber-400" : ""}`} />
                  {selectedEmail.is_starred ? "Starred" : "Star"}
                </button>
                <button
                  className="inline-flex items-center h-8 text-sm px-3 border border-gray-200 rounded-md hover:border-red-300 hover:text-red-600 transition-colors disabled:opacity-50"
                  onClick={() => { handleArchiveSingle(selectedEmailId); setSelectedEmailId(null); }}
                  disabled={archivingIds.has(selectedEmailId)}
                >
                  {archivingIds.has(selectedEmailId) ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1.5" />}
                  {archivingIds.has(selectedEmailId) ? "Archiving..." : "Archive"}
                </button>
              </div>
              <button
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                onClick={() => setSelectedEmailId(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
